// src/pages/Spot/TradeHistory.js
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Paper,
  CircularProgress,
  useTheme,
  Drawer,
  IconButton,
  useMediaQuery,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchTradeHistory } from '../../store/Slices/spotTradingSlice';

const TradeHistory = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.data);
  const { tradeHistory, tradeHistoryLoading, error } = useSelector((state) => state.spotTrading);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tradeTypeFilter, setTradeTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [symbolFilter, setSymbolFilter] = useState('');
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (user?._id) {
      dispatch(fetchTradeHistory(user._id)).unwrap().catch((err) => {
        console.error('Failed to fetch trade history:', err);
      });
    }
  }, [dispatch, user?._id]);

  const formatDate = (dateInput) => {
    if (!dateInput) return 'Invalid Date';
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const filteredTrades = useMemo(() => {
    const base = tradeHistory || [];
    const filtered = base.filter((trade) => {
      const matchesTradeType = tradeTypeFilter === 'All' || trade.trade_type === tradeTypeFilter;
      const matchesStatus = statusFilter === 'All' || trade.status === statusFilter;
      const matchesSymbol =
        symbolFilter === '' ||
        (trade.trading_pair_id?.symbol || '')
          .toLowerCase()
          .includes(symbolFilter.toLowerCase());
      return matchesTradeType && matchesStatus && matchesSymbol;
    });

    // فرز من الأحدث إلى الأقدم (باستخدام created_at من الموديل)
    return filtered.sort((a, b) => {
      const da = new Date(a.created_at || a.createdAt || 0).getTime();
      const db = new Date(b.created_at || b.createdAt || 0).getTime();
      return db - da;
    });
  }, [tradeHistory, tradeTypeFilter, statusFilter, symbolFilter]);

  const tradeTypes = ['All', ...new Set((tradeHistory || []).map((t) => t.trade_type).filter(Boolean))];
  const statuses = ['All', ...new Set((tradeHistory || []).map((t) => t.status).filter(Boolean))];

  const handleRowClick = (trade) => {
    if (!isMobile) return;
    setSelectedTrade(trade);
    setDrawerOpen(true);
  };

  if (tradeHistoryLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress sx={{ color: theme.palette.brand.main }} />
        <Typography sx={{ mt: 2, color: theme.palette.text.secondary }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography sx={{ color: theme.palette.error.main, p: 2 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 2px 10px rgba(0, 0, 0, 0.5)'
            : '0 2px 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Typography
        variant="h6"
        sx={{ mb: 3, color: theme.palette.text.primary, fontWeight: 600 }}
      >
        Trade History
      </Typography>

      {/* Filters */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <FormControl sx={{ minWidth: isMobile ? '100%' : 120 }}>
          <InputLabel sx={{ color: theme.palette.text.secondary }}>Trade Type</InputLabel>
          <Select
            value={tradeTypeFilter}
            onChange={(e) => setTradeTypeFilter(e.target.value)}
            label="Trade Type"
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? '#2A2E37' : '#F5F5F5',
              color: theme.palette.text.primary,
              borderRadius: 1,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.mode === 'dark' ? '#2A2E37' : '#E0E0E0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.brand.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.brand.main,
              },
            }}
          >
            {tradeTypes.map((type) => (
              <MenuItem
                key={type}
                value={type}
                sx={{ color: theme.palette.text.primary }}
              >
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: isMobile ? '100%' : 120 }}>
          <InputLabel sx={{ color: theme.palette.text.secondary }}>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? '#2A2E37' : '#F5F5F5',
              color: theme.palette.text.primary,
              borderRadius: 1,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.mode === 'dark' ? '#2A2E37' : '#E0E0E0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.brand.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.brand.main,
              },
            }}
          >
            {statuses.map((status) => (
              <MenuItem
                key={status}
                value={status}
                sx={{ color: theme.palette.text.primary }}
              >
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Search by Symbol"
          value={symbolFilter}
          onChange={(e) => setSymbolFilter(e.target.value)}
          sx={{
            minWidth: isMobile ? '100%' : 200,
            '& .MuiInputBase-root': {
              bgcolor: theme.palette.mode === 'dark' ? '#2A2E37' : '#F5F5F5',
              borderRadius: 1,
            },
            '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.mode === 'dark' ? '#2A2E37' : '#E0E0E0',
              },
              '&:hover fieldset': { borderColor: theme.palette.brand.main },
              '&.Mui-focused fieldset': { borderColor: theme.palette.brand.main },
            },
          }}
        />
      </Box>

      {/* Table */}
      {filteredTrades.length === 0 ? (
        <Box>
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              textAlign: 'center',
              py: 4,
            }}
          >
            No trades in history currently.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
          <Table
            sx={{
              minWidth: isMobile ? '100%' : 800,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor:
                    theme.palette.mode === 'dark' ? '#1A1C22' : '#F0F0F0',
                }}
              >
                <TableCell sx={{ color: theme.palette.brand.main, fontWeight: 'bold' }}>
                  Pair
                </TableCell>
                <TableCell sx={{ color: theme.palette.brand.main, fontWeight: 'bold' }}>
                  Type
                </TableCell>
                <TableCell sx={{ color: theme.palette.brand.main, fontWeight: 'bold' }}>
                  Amount
                </TableCell>
                {!isMobile && (
                  <>
                    <TableCell sx={{ color: theme.palette.brand.main, fontWeight: 'bold' }}>
                      Limit Price
                    </TableCell>
                    <TableCell sx={{ color: theme.palette.brand.main, fontWeight: 'bold' }}>
                      Executed Price
                    </TableCell>
                    <TableCell sx={{ color: theme.palette.brand.main, fontWeight: 'bold' }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ color: theme.palette.brand.main, fontWeight: 'bold' }}>
                      Date
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTrades.map((trade) => (
                <TableRow
                  key={trade._id}
                  onClick={() => handleRowClick(trade)}
                  sx={{
                    '&:hover': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : '#F5F5F5',
                      cursor: isMobile ? 'pointer' : 'default',
                    },
                  }}
                >
                  <TableCell sx={{ color: theme.palette.text.primary }}>
                    {trade.trading_pair_id?.symbol || 'N/A'}
                  </TableCell>
                  <TableCell
                    sx={{
                      color:
                        trade.trade_type === 'Buy'
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                    }}
                  >
                    {trade.trade_type}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>
                    {Number(trade.amount || 0).toFixed(4)}
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell sx={{ color: theme.palette.text.primary }}>
                        {trade.limit_price ? Number(trade.limit_price).toFixed(2) : '-'}
                      </TableCell>
                      <TableCell sx={{ color: theme.palette.text.primary }}>
                        {trade.executed_price
                          ? Number(trade.executed_price).toFixed(2)
                          : '-'}
                      </TableCell>
                      <TableCell
                        sx={{
                          color:
                            trade.status === 'Filled'
                              ? theme.palette.success.main
                              : theme.palette.text.secondary,
                        }}
                      >
                        {trade.status}
                      </TableCell>
                      <TableCell sx={{ color: theme.palette.text.primary }}>
                        {formatDate(trade.created_at || trade.createdAt)}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Drawer للموبايل */}
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            p: 2,
            maxHeight: '80vh',
          },
        }}
      >
        {selectedTrade && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: theme.palette.text.primary }}
              >
                Trade Details
              </Typography>
              <IconButton onClick={() => setDrawerOpen(false)}>
                <CloseIcon sx={{ color: theme.palette.text.secondary }} />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 2, bgcolor: theme.palette.divider }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 'bold',
                  }}
                >
                  Pair
                </Typography>
                <Typography sx={{ color: theme.palette.text.primary }}>
                  {selectedTrade.trading_pair_id?.symbol || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 'bold',
                  }}
                >
                  Type
                </Typography>
                <Typography
                  sx={{
                    color:
                      selectedTrade.trade_type === 'Buy'
                        ? theme.palette.success.main
                        : theme.palette.error.main,
                  }}
                >
                  {selectedTrade.trade_type}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 'bold',
                  }}
                >
                  Amount
                </Typography>
                <Typography sx={{ color: theme.palette.text.primary }}>
                  {Number(selectedTrade.amount || 0).toFixed(4)}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 'bold',
                  }}
                >
                  Limit Price
                </Typography>
                <Typography sx={{ color: theme.palette.text.primary }}>
                  {selectedTrade.limit_price
                    ? Number(selectedTrade.limit_price).toFixed(2)
                    : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 'bold',
                  }}
                >
                  Executed Price
                </Typography>
                <Typography sx={{ color: theme.palette.text.primary }}>
                  {selectedTrade.executed_price
                    ? Number(selectedTrade.executed_price).toFixed(2)
                    : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 'bold',
                  }}
                >
                  Status
                </Typography>
                <Typography
                  sx={{
                    color:
                      selectedTrade.status === 'Filled'
                        ? theme.palette.success.main
                        : theme.palette.text.secondary,
                  }}
                >
                  {selectedTrade.status}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 'bold',
                  }}
                >
                  Date
                </Typography>
                <Typography sx={{ color: theme.palette.text.primary }}>
                  {formatDate(selectedTrade.created_at || selectedTrade.createdAt)}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>
    </Paper>
  );
};

export default TradeHistory;
